import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RuleIcon from "@mui/icons-material/Rule";
import { toast } from "react-toastify";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  getSpiffSettings,
  resetSpiffSettings,
  updateSpiffSettings,
} from "services/spiffService";

const initialSettings = {
  requiredApprovals: "3",
  amount: "750",
  minimumWithdrawAmount: "750",
  resetOnRejection: true,
  isActive: true,
  termsAndConditions: [""],
};

function SpiffSettings() {
  const [settings, setSettings] = useState(initialSettings);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");

  const applySettings = (data) => {
    setSettings({
      requiredApprovals: (data?.requiredApprovals ?? 3).toString(),
      amount: (data?.amount ?? 750).toString(),
      minimumWithdrawAmount: (data?.minimumWithdrawAmount ?? 750).toString(),
      resetOnRejection: Boolean(data?.resetOnRejection),
      isActive: Boolean(data?.isActive),
      termsAndConditions: data?.termsAndConditions?.length ? data.termsAndConditions : [""],
    });
    setLastUpdated(data?.updatedAt || "");
  };

  const fetchSettings = async () => {
    setFetchLoading(true);
    try {
      const response = await getSpiffSettings();
      if (response.success) applySettings(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch SPIFF settings");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleTermChange = (index, value) => {
    setSettings((prev) => ({
      ...prev,
      termsAndConditions: prev.termsAndConditions.map((term, termIndex) =>
        termIndex === index ? value : term
      ),
    }));
  };

  const handleAddTerm = () => {
    setSettings((prev) => ({
      ...prev,
      termsAndConditions: [...prev.termsAndConditions, ""],
    }));
  };

  const handleRemoveTerm = (index) => {
    setSettings((prev) => ({
      ...prev,
      termsAndConditions:
        prev.termsAndConditions.length === 1
          ? [""]
          : prev.termsAndConditions.filter((_, termIndex) => termIndex !== index),
    }));
  };

  const validateSettings = () => {
    if (Number(settings.requiredApprovals) < 1) {
      toast.error("Required approvals must be at least 1");
      return false;
    }
    if (Number(settings.amount) < 0 || Number(settings.minimumWithdrawAmount) < 0) {
      toast.error("Amount fields cannot be negative");
      return false;
    }
    return true;
  };

  const handleSaveSettings = async () => {
    if (!validateSettings()) return;

    setSaving(true);
    try {
      const payload = {
        requiredApprovals: Number(settings.requiredApprovals),
        amount: Number(settings.amount),
        minimumWithdrawAmount: Number(settings.minimumWithdrawAmount),
        resetOnRejection: settings.resetOnRejection,
        isActive: settings.isActive,
        termsAndConditions: settings.termsAndConditions.map((term) => term.trim()).filter(Boolean),
      };
      const response = await updateSpiffSettings(payload);
      if (response.success) {
        applySettings(response.data);
        toast.success("SPIFF settings updated successfully");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update SPIFF settings");
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = async () => {
    setSaving(true);
    try {
      const response = await resetSpiffSettings();
      if (response.success) {
        applySettings(response.data);
        toast.success("SPIFF settings reset successfully");
      }
    } catch (error) {
      toast.error(error.message || "Failed to reset SPIFF settings");
    } finally {
      setSaving(false);
    }
  };

  const formattedUpdatedAt = lastUpdated ? new Date(lastUpdated).toLocaleString() : "Not saved yet";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox
              display="flex"
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              mb={3}
              pb={2}
              borderBottom="1px solid #e9ecef"
            >
              <ArgonBox display="flex" alignItems="center" gap={1.5}>
                <EmojiEventsIcon color="primary" sx={{ fontSize: 32 }} />
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    SPIFF Settings
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Last updated: {formattedUpdatedAt}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox display="flex" alignItems="center" gap={1.5}>
                <ArgonTypography variant="button" fontWeight="medium">
                  {settings.isActive ? "Active" : "Inactive"}
                </ArgonTypography>
                <Switch
                  checked={settings.isActive}
                  onChange={(event) => handleSettingChange("isActive", event.target.checked)}
                  disabled={fetchLoading || saving}
                />
              </ArgonBox>
            </ArgonBox>

            {fetchLoading ? (
              <ArgonBox textAlign="center" py={5}>
                <CircularProgress color="primary" />
                <ArgonTypography variant="body2" color="text" mt={2}>
                  Loading SPIFF settings...
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="caption" fontWeight="bold">
                      Required Approvals
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="number"
                      value={settings.requiredApprovals}
                      onChange={(event) => handleSettingChange("requiredApprovals", event.target.value)}
                      disabled={saving}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="caption" fontWeight="bold">
                      Incentive Amount
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="number"
                      value={settings.amount}
                      onChange={(event) => handleSettingChange("amount", event.target.value)}
                      disabled={saving}
                      inputProps={{ min: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencyRupeeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="caption" fontWeight="bold">
                      Minimum Withdraw Amount
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="number"
                      value={settings.minimumWithdrawAmount}
                      onChange={(event) =>
                        handleSettingChange("minimumWithdrawAmount", event.target.value)
                      }
                      disabled={saving}
                      inputProps={{ min: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencyRupeeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
                      <ArgonTypography variant="caption" fontWeight="bold">
                        Reset streak on rejection
                      </ArgonTypography>
                      <Switch
                        checked={settings.resetOnRejection}
                        onChange={(event) =>
                          handleSettingChange("resetOnRejection", event.target.checked)
                        }
                        disabled={saving}
                      />
                    </ArgonBox>
                  </Grid>
                </Grid>

                <ArgonBox mt={3}>
                  <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <RuleIcon color="primary" fontSize="small" />
                      <ArgonTypography variant="h6" fontWeight="medium">
                        Terms and Conditions
                      </ArgonTypography>
                    </ArgonBox>
                    <Tooltip title="Add term">
                      <span>
                        <IconButton color="primary" onClick={handleAddTerm} disabled={saving}>
                          <AddIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ArgonBox>

                  <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                    {settings.termsAndConditions.map((term, index) => (
                      <ArgonBox key={`spiff-term-${index}`} display="flex" gap={1}>
                        <ArgonInput
                          fullWidth
                          value={term}
                          placeholder={`Term ${index + 1}`}
                          onChange={(event) => handleTermChange(index, event.target.value)}
                          disabled={saving}
                        />
                        <Tooltip title="Remove term">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveTerm(index)}
                              disabled={saving}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ArgonBox>
                    ))}
                  </ArgonBox>
                </ArgonBox>

                <ArgonBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1.5} mt={4} pt={3} borderTop="1px solid #e9ecef">
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleResetSettings}
                    disabled={saving}
                  >
                    <RestartAltIcon fontSize="small" />
                    &nbsp;Reset Default
                  </ArgonButton>
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSettings}
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon fontSize="small" />}
                    &nbsp;Save Settings
                  </ArgonButton>
                </ArgonBox>
              </>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SpiffSettings;
